import { useTranslation } from 'react-i18next'
import './styles.scss'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { getLocalAppSettings, getLocalFreeMenus, getLocalLoggedInStatus, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { commonMethods } from 'Utils/commonMethods'
import UserGuide from 'Screens/UserGuide'
import Subscription from 'Screens/Subscription'
import SupportRequest from 'Screens/SupportRequest'
import AboutUs from 'Screens/AboutUs'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { setUser } from '@sentry/react'
import CoffeeGames from 'Screens/CoffeeGames'
import CoffeeCommunities from './Components/CoffeeCommunities/index'
import CoffeeCast from './Components/CoffeeCast'
import CoffeeCart from './Components/CoffeeCart'

// --- Utility Functions ---

const normalizeUrl = (url) => url?.replace(/^\//, '')?.toLowerCase()

const validityFormatter = (date, dateFormatBasedOnUser) => {
  const [validityString, validTill] = date.split(':').map((el) => el.trim())

  return `${validityString}: ${dateFormatBasedOnUser(validTill, 'spaceFormat')}`
}

function generateLinearGradientColor(userDetails) {
  const defaultColor = '#50b3f6'
  const color = userDetails?.sub?.subscriptionColor || defaultColor

  const colorPart1 = color.slice(0, 7)
  const colorPart2 = color.slice(9, 16)
  const colorPart3 = color.slice(18, 27)

  return `linear-gradient(100deg, ${colorPart1}, ${colorPart2}, ${colorPart3})`
}

const CoffeeWebSocial = () => {
  const history = useHistory()
  const { i18n, t } = useTranslation()
  const location = useLocation()
  const { dateFormatBasedOnUser } = commonMethods

  const userLoginStatus = getLocalLoggedInStatus()
  const userDetails = getLocalUserDetails()
  const appSettings = getLocalAppSettings()
  const locationRef = useRef()

  const [active, setActive] = useState('')
  const [isTrialUser, setIsTrialUser] = useState(userDetails?.isTrialUser)
  const [menuItems, setMenuItems] = useState([])
  const [showAboutUsNavLogo, setShowAboutUsNavLogo] = useState(true)
  const [userGeolocation, setUserGeolocation] = useState(null)
  const previousViewRef = useRef(null)

  const parentMenuWithSubmenus = userDetails?.menus?.find((menu) => menu.subMenuModels?.length > 0)

  const freeMenusToDisplay = getLocalFreeMenus()

  // Handle view param on initial load and when it changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const view = params.get('view')
    const prev = params.get('prev')

    // Immediately hide icon if view is about-coffeeweb (handles refresh case)
    if (view && normalizeUrl(view) === 'about-coffeeweb') {
      setShowAboutUsNavLogo(false)
    }

    if (menuItems?.length > 0) {
      const normalizedMenuUrls = menuItems.map((m) => normalizeUrl(m.menuUrl))
      const allowedViews = [...normalizedMenuUrls, 'about-coffeeweb']

      if (view && allowedViews.includes(view.toLowerCase())) {
        const normalizedView = view.toLowerCase()

        setActive(normalizedView)

        // Store the previous view before setting about-coffeeweb
        if (normalizedView === 'about-coffeeweb') {
          // If we're on about-coffeeweb, hide the icon
          setShowAboutUsNavLogo(false)
          // Use prev param from URL if available (handles refresh), otherwise use stored ref or first menu item
          const normalizedPrev = prev ? normalizeUrl(prev) : null

          if (normalizedPrev && allowedViews.includes(normalizedPrev)) {
            previousViewRef.current = normalizedPrev
            locationRef.current = previousViewRef.current
          } else if (!previousViewRef.current && menuItems.length > 0) {
            previousViewRef.current = normalizeUrl(menuItems[0]?.menuUrl)
            locationRef.current = previousViewRef.current
          } else {
            locationRef.current = previousViewRef.current || normalizeUrl(menuItems[0]?.menuUrl)
          }
        } else {
          // If we're on a regular menu item, show the icon and store it as previous
          setShowAboutUsNavLogo(true)
          previousViewRef.current = normalizedView
          locationRef.current = normalizedView
        }
      } else {
        const defaultMenuUrl = normalizeUrl(menuItems[0]?.menuUrl)

        locationRef.current = normalizeUrl(menuItems[0]?.menuUrl)
        previousViewRef.current = defaultMenuUrl

        setActive(defaultMenuUrl)
        history.replace(`${location.pathname}?view=${defaultMenuUrl}`)
      }
    }
  }, [menuItems, location.search])

  // useEffect(() => {
  //   const submenuItems = parentMenuWithSubmenus?.subMenuModels || []

  //   setMenuItems(submenuItems)
  // }, [userDetails?.id])

  useEffect(() => {
    const parentMenuWithSubmenus = userDetails?.id ? userDetails?.menus?.find((menu) => menu.subMenuModels?.length > 0) : freeMenusToDisplay.find((menu) => menu.subMenuModels?.length > 0)

    const submenuItems = parentMenuWithSubmenus?.subMenuModels || []

    // Prevent unnecessary updates to state if submenuItems hasn't changed
    if (JSON.stringify(submenuItems) !== JSON.stringify(menuItems)) {
      setMenuItems(submenuItems)
    }
  }, [userDetails?.id, freeMenusToDisplay, menuItems]) // Added menuItems to dependency array for comparison

  useEffect(() => {
    getGeolocation()
  }, [])

  const rawComponentMap = {
    support: SupportRequest,
    'user-guide': UserGuide,
    coffeecart: CoffeeCart,
    coffeecast: CoffeeCast,
    'coffee-games': CoffeeGames,
    'coffee-community': CoffeeCommunities,
    'about-coffeeweb': AboutUs // Note: camelCase or PascalCase — doesn't matter
  }

  const componentMap = Object.entries(rawComponentMap).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value

    return acc
  }, {})

  const rightContent = () => {
    let activeItem = menuItems.find((el) => normalizeUrl(el.menuUrl) === active)

    if (!activeItem && normalizeUrl(active) === normalizeUrl('about-coffeeweb')) {
      activeItem = {
        menuName: 'About CoffeeWeb',
        menuUrl: 'about-coffeeweb'
      }
    }

    if (!activeItem) return null

    const Component = componentMap[normalizeUrl(activeItem.menuUrl)]

    if (!Component) return <div className="coming-soon-component">{activeItem.menuUrl} Coming Soon</div>

    return <Component componentLabel={activeItem.menuName} setActive={setActive} normalizeUrl={normalizeUrl} locationRef={locationRef} setShowAboutUsNavLogo={setShowAboutUsNavLogo} />
  }

  function generateLinearGradientColor() {
    const defaultColor = '#50b3f6'
    const color = userDetails?.id ? userDetails?.sub?.subscriptionColor || '#50b3f6, #8DD5F4, #50b3f6' : '#ae5e49, #d89a8b, #ae5e49'

    const colorPart1 = color.slice(0, 7)
    const colorPart2 = color.slice(9, 16)
    const colorPart3 = color.slice(18, 27)

    return `linear-gradient(100deg, ${colorPart1}, ${colorPart2}, ${colorPart3})`
  }

  const validityFormatter = (date) => {
    const [validityString, validTill] = date.split(':').map((el) => el.trim())

    return `${validityString}: ${dateFormatBasedOnUser(validTill, 'spaceFormat')}`
  }

  const navigateToSubscription = (event) => {
    event.stopPropagation()
    history.push(ROUTE_STRINGS.subscription)
  }

  const handleMenuClick = (menuUrl) => {
    const cleanUrl = normalizeUrl(menuUrl)

    setActive(cleanUrl)
    history.push(`${location.pathname}?view=${cleanUrl}`)
  }

  const getGeolocation = async () => {
    try {
      if (!userDetails) {
        const countryList = await apiAdapterCoffeeWeb.getAllCountries()
        const locationResponse = await commonMethods.searchLocation()

        if (locationResponse && countryList?.data?.length > 0) {
          const matchedCountry = countryList?.data?.find((c) => {
            const countryName = locationResponse.country.toLowerCase()

            return c?.nicename?.toLowerCase() === countryName || c?.name?.toLowerCase() === countryName
          })

          const result = matchedCountry ? { nicename: matchedCountry.nicename || matchedCountry.name, flagsUrl: matchedCountry.flagsUrl } : null

          setUserGeolocation({ ...result, city: locationResponse?.name || '', region: locationResponse?.region || '' })
        }
      }
    } catch {
      setUserGeolocation(null)
    }
  }

  const handleLogin = () => {
    if (userLoginStatus === false) {
      history.push(ROUTE_STRINGS.login)
    } else {
      history.push(ROUTE_STRINGS.createAccount)
    }
  }

  const AvatarCard = () => (
    <div className="avatar-container">
      <div
        className={`avatar-card ${userDetails?.id ? '' : 'guest-user-avatar-card'}`}
        onClick={() => {
          // setModalShow(true)
          // trackEvent(USER_EVENTS.PROFILE_SCREEN) // user-analytics
        }}
        style={{
          '--custom-background': generateLinearGradientColor(userDetails)
        }}
        data-testid="avatar-card"
      >
        <div className="avatarDiv" data-testid="avatar-div-container">
          <div>
            <img alt="" className={`avatarUserCountryImg ${userDetails?.id ? '' : 'guest'}`} src={userDetails?.id ? userDetails?.countryFlag : userGeolocation?.flagsUrl || 'https://coffeeweb.s3.ap-south-1.amazonaws.com/CoffeeWebLogoWhite.png'} title="avatar-country-flag" />
          </div>
          <div className="avatar-user-personal-details" data-testid="user-personal-details-container">
            <div className="username-edit-button" data-testid="user-name-container">
              <div>
                {userDetails?.id ? (
                  <>
                    {userDetails?.firstName} {userDetails?.lastName}
                  </>
                ) : (
                  <>Guest User</>
                )}
              </div>
              {/* Commented below pencil icon for edit profile as per discussion */}
              {/* <div>
                <i className="pi pi-pencil" data-testid="pencil-icon-container" />
              </div> */}
            </div>
            {!userDetails?.id && (
              <div className="user-location" data-testid="user-location-container">
                {userGeolocation?.city && `${userGeolocation?.city}, `}
                {userGeolocation?.nicename && ` ${userGeolocation?.nicename}`}
              </div>
            )}
            {userDetails?.occupationName !== 'NA' && (
              <div className="user-occupation" data-testid="user-occupation-container">
                {userDetails?.occupationName}
              </div>
            )}
          </div>
        </div>

        {userDetails?.phone && (
          <div className="user-field" data-testid="user-phone-container">
            <span className="label">{t('PHONE')} : + </span>
            <span className="value">
              {userDetails?.countryPhoneCode} {userDetails?.phone}
            </span>
          </div>
        )}

        {userDetails?.email && (
          <div className="user-field" data-testid="user-email-container">
            <span className="label">{t('EMAIL')} : </span>
            <span className="value">{userDetails?.email}</span>
          </div>
        )}

        <div className={`userSubscriptionDetails ${userDetails ? 'login-user' : ''}`} data-testid="user-subscription-details-container">
          <div className="subscriptionDate" data-testid="subscription-date-and-valid-days-container">
            {userDetails?.sub?.endDate && userDetails?.sub?.subType !== 1 && <div data-testid="subscription-valid-days-container"> {userDetails?.sub?.subscriptionValidTill}</div>}
            {userDetails?.sub?.endDate && userDetails?.sub?.subType !== 1 && <div data-testid="subscription-valid-date-container"> {validityFormatter(userDetails?.sub?.subscriptionValidTillDate, dateFormatBasedOnUser)}</div>}
          </div>
          <div className="subscriptionType" data-testid="user-subscription-name">
            {isTrialUser ? t('TRIAL_PLATINUM') : userDetails?.sub?.subTypeName}
          </div>
        </div>
        {!userDetails?.id && (
          <div className="user-location" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '16px' }} data-testid="user-location-container">
            <div>
              {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
            </div>
            <div>{new Date().toLocaleTimeString('en-GB', { hour12: true, hour: '2-digit', minute: '2-digit' })?.toUpperCase()}</div>
          </div>
        )}
        {appSettings?.showrenewal && userDetails?.sub?.renewProcess && (
          <div className="upgrade-button-large-screen-container" data-testid="upgrade-button-container">
            {appSettings?.showrenewal && (
              <button className="upgradeButtonLargeScreen" data-testid="upgrade-action-container" onClick={navigateToSubscription}>
                {isTrialUser ? t('BUY_NOW') : userDetails?.sub?.renewProcess}
              </button>
            )}
          </div>
        )}
        {!userDetails?.id && (
          <div className="upgrade-button-large-screen-container" data-testid="upgrade-button-container">
            <button className="upgradeButtonLargeScreen" data-testid="upgrade-action-container" onClick={handleLogin}>
              {t('LOGIN_AND_CREATE_ACCOUNT')}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  // const currentPath = userDetails?.id ? location.pathname.split('/')[2] : location.pathname.split('/')[1] // commented if we need to use anywhere

  return (
    <div className="coffeeweb-page-container">
      <div className={`coffeeweb-page coffeeweb-theme ${userDetails?.id ? '' : 'guest-user'}`}>
        <aside className={`left-panel ${userDetails?.id ? '' : 'guest-user-left-panel'}`}>
          <AvatarCard />
          <div className="menu-items-container">
            <div className="menu">
              {menuItems.map((m) => (
                <div
                  key={m.key}
                  className={`menu-items ${normalizeUrl(m.menuUrl) === active ? 'active' : ''}`}
                  onClick={() => {
                    const normalizedUrl = normalizeUrl(m.menuUrl)

                    handleMenuClick(m.menuUrl)
                    setShowAboutUsNavLogo(true)
                    locationRef.current = normalizedUrl
                    previousViewRef.current = normalizedUrl
                  }}
                >
                  <img style={{ width: '34px' }} src={m?.menuIcon} alt={m.menuUrl} />
                  <span style={{ marginLeft: '8px', fontSize: '20px' }}>{m?.menuName}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main className={`right-panel ${userDetails?.id ? '' : 'guest-user'}`}>
          <div className="right-content">{rightContent()}</div>
        </main>
      </div>
      {showAboutUsNavLogo && (
        <img
          src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Dev-AboutCoffeeWeb.png"
          alt=""
          className="about-coffee-nav"
          onClick={() => {
            // Store current active view as previous before navigating to about-coffeeweb
            const prevView = active && active !== 'about-coffeeweb' ? active : previousViewRef.current || (menuItems.length > 0 ? normalizeUrl(menuItems[0]?.menuUrl) : '')

            if (prevView) {
              previousViewRef.current = prevView
              locationRef.current = prevView
              // Navigate with prev parameter to preserve it on refresh
              // The useEffect will handle setting the active state
              history.push(`${location.pathname}?view=about-coffeeweb&prev=${prevView}`)
            } else {
              handleMenuClick('about-coffeeWeb')
            }
            setShowAboutUsNavLogo(false)
          }}
        />
      )}
    </div>
  )
}

export default CoffeeWebSocial
